import {
  Group,
  Table as FigTable,
  Paragraph,
  Loader,
  useLanguage,
  createStyles,
} from '@ingeniorforeningen/figurine-core';
import { AppLanguage, UiCase } from '../../types';
import { stripText } from '../../helpers/textsHelpers';
import { ROUTE } from '../../App';

type Props = {
  cypress?: string;
  cases?: UiCase[] | null;
  loading: boolean;
  noCasesText?: string;
  language: string;
};

const style = createStyles((theme) => ({
  row: {
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: `${theme.other.colorTokens.backgrounds.primary} !important`,
      color: theme.other.colorTokens.body.light,
    },
  },
  link: {
    textDecoration: 'none',
    display: 'contents',
    color: 'inherit',
    td: {
      padding: theme.spacing.md,
      whiteSpace: 'normal',
      borderBottom: `1px solid ${theme.other.colorTokens.dividers.dark}`,
      verticalAlign: 'middle',
    },
  },
  group: {
    width: '100%',
  },
}));

export const Table = ({ cases, loading, noCasesText, language, cypress }: Props) => {
  const { isEnglish } = useLanguage();
  const { classes } = style();

  if (loading) {
    return (
      <>
        <FigTable striped mb="xl" cypress={cypress ?? 'casesList'}>
          <thead></thead>
          <tbody></tbody>
        </FigTable>
        <Group
          m="xs"
          align="center"
          justify="center"
          direction="row"
          className={classes.group}
          cypress="skeleton"
        >
          <Loader size="xl" />
          <Paragraph
            text={isEnglish ? 'Retrieving your cases...' : 'Henter dine sager...'}
            strong={true}
          />
        </Group>
      </>
    );
  }

  if (cases) {
    const columnNames = Object.keys(cases[0]).map((column) => column);
    return (
      <>
        <FigTable striped mb="xl" cypress={cypress ?? 'casesList'}>
          <thead>
            <tr>
              {columnNames.map((column) => column !== 'Id' && <th key={column}>{column}</th>)}
            </tr>
          </thead>

          <tbody>
            {cases.map((c, index) => (
              <tr className={classes.row} key={`tr_${index}`}>
                <a
                  className={classes.link}
                  href={
                    language === AppLanguage.English
                      ? `${ROUTE.CASE_DETAILS.ENGLISH}/${c.Id}`
                      : `${ROUTE.CASE_DETAILS.DANISH}/${c.Id}`
                  }
                >
                  {columnNames.map(
                    (column, index) =>
                      column !== 'Id' && (
                        <td
                          style={{ whiteSpace: index === 2 ? 'nowrap' : 'normal' }}
                          key={`row_${column}`}
                        >
                          {c[column]}
                        </td>
                      ),
                  )}
                </a>
              </tr>
            ))}
          </tbody>
        </FigTable>
      </>
    );
  }

  return <Paragraph text={stripText(noCasesText)} />;
};
